import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, PaperAirplaneIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { authService } from '../../services/authService';
import { Campaign } from '../../services/campaignService';
import { replaceVariablesWithSampleData } from '../../utils/previewUtils';
import { highlightVariables } from '../../utils/variableHighlighting';


interface Message {
  id: string;
  type: 'user' | 'ai' | 'system';
  content: string;
  timestamp: Date;
}

interface CampaignAIEditorProps {
  campaign: Campaign;
  onSave: (updatedCampaign: Campaign) => void;
  onClose: () => void;
}

const CampaignAIEditor: React.FC<CampaignAIEditorProps> = ({ campaign, onSave, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentContent, setCurrentContent] = useState(campaign.content_html);
  const [isPreviewMode, setIsPreviewMode] = useState(true); // Default to preview mode with sample data
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load initial system message on mount
  useEffect(() => {
    // Add initial system message
    setMessages([{
      id: '1',
      type: 'system',
      content: `Ready to edit "${campaign.name}". Give me instructions like "make the tone more friendly" or "add more emphasis on special needs support" and I'll update your campaign content.`,
      timestamp: new Date()
    }]);
  }, [campaign.id, campaign.name]);

  // Auto scroll to bottom of messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Get content with preview mode variable replacement and highlighting
  const getDisplayContent = () => {
    const content = currentContent || '';

    if (isPreviewMode) {
      // In preview mode, replace variables with sample data and don't highlight
      return replaceVariablesWithSampleData(content);
    } else {
      // In edit mode, highlight variables with blue styling
      return highlightVariables(content);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaign.id}/edit-with-ai`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          instruction: userMessage.content,
          personalization_level: 'medium' // Could be made configurable
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Update current content
      setCurrentContent(result.content_html);

      // Add AI response
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: `✅ **Edit Applied:** ${result.change_summary}\n\nThe campaign content has been updated. You can continue giving me more instructions or save the changes.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);

      // Edit completed successfully

    } catch (error) {
      console.error('Error editing campaign:', error);

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'system',
        content: `❌ Sorry, I couldn't process that edit. Please try again or contact support if the problem persists.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };


  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">AI Campaign Editor</h2>
            <p className="text-sm text-gray-600">Editing: {campaign.name}</p>
          </div>
          <div className="flex items-center space-x-2">
            {/* Variable Preview Toggle */}
            <div className="flex bg-gray-100 rounded-md p-1">
              <button
                onClick={() => setIsPreviewMode(true)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  isPreviewMode
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Show variables with sample data (e.g., Emma instead of {$child_name})"
              >
                Sample Data
              </button>
              <button
                onClick={() => setIsPreviewMode(false)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  !isPreviewMode
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Show raw variables (e.g., {$child_name})"
              >
                Variables
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Chat Interface */}
          <div className="flex-1 flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.type === 'user'
                      ? 'bg-blue-500 text-white'
                      : message.type === 'system'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-gray-200 text-gray-900'
                  }`}>
                    <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                    <div className={`text-xs mt-1 ${
                      message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-200 text-gray-900 px-4 py-2 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      <span className="text-sm">AI is editing your campaign...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t">
              <div className="flex space-x-2">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Tell me how to edit the campaign... (e.g., 'make it more personal' or 'add urgency to the call-to-action')"
                  className="flex-1 resize-none border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>

          {/* Content Preview Sidebar */}
          <div className="w-1/2 border-l flex flex-col">
            <div className="border-b p-4">
              <h3 className="font-medium text-gray-900">Current Content</h3>
              <p className="text-sm text-gray-600 mt-1">AI edits are automatically saved to version history</p>
            </div>

            {/* Content Preview */}
            <div className="flex-1 overflow-y-auto p-4">
              <div
                className="prose prose-lg max-w-none [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:mb-3 [&_ol]:mb-3 [&_li]:mb-1 prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:font-semibold prose-em:italic prose-ul:list-disc prose-ul:ml-6 prose-ol:list-decimal prose-ol:ml-6"
                dangerouslySetInnerHTML={{ __html: getDisplayContent() }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignAIEditor;